import { useEffect, useMemo, useState } from 'react'

import { type GrapeAPIErrorType } from 'components/hooks/GrapeAPIError'
import { type GrapeMoney } from 'types'
import { convertMoneyToGrapeMoney } from 'utils'
import {
  useGetMemberOnboardingEnrollmentEventsEnrollmentEventIdMedicalPlans as useGetMedicalPlans,
} from 'utils/web_api'
import { type GetMemberOnboardingEnrollmentEventsEnrollmentEventIdMedicalPlansMetalTierItem as MetalTier } from 'utils/web_api.schemas'

import { MedicalDetails } from './MedicalDetails'
import { SmartBudgetPlan } from './Plan'
import PlanCard from './PlanCard/PlanCard'
import { type MedicalPlanDisplayItem } from '../../medicalPlanTypes'
import { medicalPlanToPlanData, STRATEGY_CONFIGS } from '../../smartBudgetUtils'
import {
  type BenefitsBundleStrategy,
  type CoverageType,
  type PlansForBenefitsBundle,
} from '../../types'

interface SmartBudgetMedicalPlanProps {
  enrollmentEventId: string,
  viewingCoverageType: CoverageType | null,
  setViewingCoverageType: (planType: CoverageType | null) => void,
  remainingAllowance: number,
  onSelectPlan: (plan: MedicalPlanDisplayItem | null) => void,
  isIndividual: boolean,
  allowanceAmount: GrapeMoney,
  minimumMetalTier: MetalTier,
  budgetStrategy: BenefitsBundleStrategy,
}

/*
const sendRecommendationEvent = (strategy: BenefitsBundleStrategy, plan?: MedicalPlanDisplayItem) => {
  window.segment?.track(`[EE] Smart budget ${strategy} recommendation`, {
    strategy,
    medical_plan_id: plan?.plan.id,
    medical_plan_name: plan?.plan.display_name,
    medical_plan_carrier: plan?.plan.carrier_name,
    medical_plan_premium: plan?.total_premium?.formatted_amount,
  })
}
*/

export function SmartBudgetMedicalPlan ({
  enrollmentEventId,
  viewingCoverageType,
  setViewingCoverageType,
  remainingAllowance,
  onSelectPlan,
  isIndividual,
  allowanceAmount,
  minimumMetalTier,
  budgetStrategy,
}: SmartBudgetMedicalPlanProps) {
  // Store the plan the user has selected for each budget strategy
  const [selectedPlans, setSelectedPlans] = useState<Record<BenefitsBundleStrategy, MedicalPlanDisplayItem | null>>({
    flexible: null,
    balanced: null,
    traditional: null,
  })

  // Only re-fires if the enrollment event ID changes
  const {
    data: medicalPlanData,
    isLoading,
    error,
  } = useGetMedicalPlans(
    enrollmentEventId,
    {
      expected_plan_utilization: undefined,
      oop_budget: undefined,
      'sort[]': ['deductible'],
      'metal_tier[]': undefined,
      'plan_type[]': undefined,
      'carrier_name[]': undefined,
      'provider_npis[]': undefined,
      generate_recommendations: false,
      'page[number]': 1,
      'page[size]': 1000,
    },
    {
      query: {
        retry: false,
        queryKey: [
          'enrollmentEvent',
          enrollmentEventId,
          'smart_budget',
        ],
      },
    }
  )

  const medicalPlans = (medicalPlanData?.data || []) as MedicalPlanDisplayItem[]

  // Get the mutation function for updating recommendations
  // const { mutate: saveRecommendations } = useUpdateSmartBudgetRecommendations()

  // Memoize the plans for each budget strategy
  const smartBudgetData: Record<BenefitsBundleStrategy, PlansForBenefitsBundle> = useMemo(() => {
    const strategiesAndPlans = {
      flexible: STRATEGY_CONFIGS.flexible.findPlans(medicalPlans, allowanceAmount, minimumMetalTier),
      balanced: STRATEGY_CONFIGS.balanced.findPlans(medicalPlans, allowanceAmount, minimumMetalTier),
      traditional: STRATEGY_CONFIGS.traditional.findPlans(medicalPlans, allowanceAmount, minimumMetalTier),
    }

    return strategiesAndPlans
  }, [medicalPlans, allowanceAmount, minimumMetalTier])

  // Send analytics events when recommendations change
  /*
  useEffect(() => {
    if (smartBudgetData) {
      try {
        sendRecommendationEvent('flexible', smartBudgetData.flexible.recommended_plan)
        sendRecommendationEvent('balanced', smartBudgetData.balanced.recommended_plan)
        sendRecommendationEvent('traditional', smartBudgetData.traditional.recommended_plan)
      } catch (error) {
        // Gracefully ignore errors, but report to Sentry
        Sentry.captureException(error)
      }
    }
  }, [smartBudgetData])

  // Send recommendations to the backend when they change
  useEffect(() => {
    if (smartBudgetData) {
      saveRecommendations(
        {
          enrollmentEventId,
          data: {
            flexible: smartBudgetData.flexible.recommended_plan?.plan.id,
            balanced: smartBudgetData.balanced.recommended_plan?.plan.id,
            traditional: smartBudgetData.traditional.recommended_plan?.plan.id,
          },
        },
        {
          onError: (error) => {
            // Gracefully ignore errors, but report to Sentry
            Sentry.captureException(error)
          },
        }
      )
    }
  }, [smartBudgetData, enrollmentEventId, saveRecommendations])
  */

  const medicalPlanForStrategy = useMemo(() => {
    const strategyData = smartBudgetData[budgetStrategy]
    const planForStrategy = selectedPlans[budgetStrategy]

    // Make sure the plan we have selected is in the list of plans for the strategy
    if (planForStrategy) {
      const foundPlan = strategyData?.all_plans_for_strategy?.find(
        plan => plan.plan.id === planForStrategy?.plan.id
      )
      if (foundPlan) return foundPlan
      setSelectedPlans((prev) => ({
        ...prev,
        [budgetStrategy]: null,
      }))
    }

    // Fallback to recommended plan
    return strategyData?.recommended_plan
  }, [smartBudgetData, budgetStrategy, selectedPlans])

  // Bubble up the selected plan to the parent when it changes
  useEffect(() => {
    onSelectPlan(medicalPlanForStrategy || null)
  }, [medicalPlanForStrategy, onSelectPlan])

  if (isLoading) {
    return (
      <PlanCard.Loading />
    )
  }

  if (error) {
    return (
      <PlanCard.Error coverageType='medical' error={error as GrapeAPIErrorType} />
    )
  }

  if (!medicalPlanForStrategy) {
    return (
      <PlanCard.Empty>
        <div className='text-gray-500'>No medical plans work for this budget strategy</div>
      </PlanCard.Empty>
    )
  }

  return (
    <SmartBudgetPlan
      allPlans={smartBudgetData[budgetStrategy]?.all_plans_for_strategy || []}
      selectedPlan={medicalPlanForStrategy}
      getPlanKey={(plan) => plan.plan.id}
      planToPlanData={(plan) => medicalPlanToPlanData(plan, isIndividual)}
      planToPlanPremium={(plan) =>
        convertMoneyToGrapeMoney(plan.total_premium)}
      remainingAllowance={remainingAllowance}
      coverageType='medical'
      viewingCoverageType={viewingCoverageType}
      setViewingCoverageType={setViewingCoverageType}
      onSelectPlan={(plan) => {
        setSelectedPlans((prev) => ({
          ...prev,
          [budgetStrategy]: plan,
        }))
        onSelectPlan(plan)
      }}
      renderPlanDetails={(plan) => <MedicalDetails medicalPlan={plan} enrollmentEventId={enrollmentEventId} isIndividual={isIndividual} />}
      popoverTitle='Similar medical plans for this budget strategy:'
    />
  )
}

SmartBudgetMedicalPlan.displayName = 'SmartBudget.MedicalPlan'
